import { Image03Icon, Lock01Icon, SlashCircle01Icon } from "@/icons";
import { Badge } from "@/kit/ui/badge";
import React, { useId } from "react";
import { FormattedMessage } from "react-intl";
import { graphql, useFragment } from "react-relay";
import { generatePath, Link } from "react-router";
import { cva, type VariantProps } from "class-variance-authority";
import { ActivityCardHostFragment$key } from "./__generated__/ActivityCardHostFragment.graphql";
import { ActivityCardTagListFragment$key } from "./__generated__/ActivityCardTagListFragment.graphql";
import { EventLocationFragment$key } from "./__generated__/EventLocationFragment.graphql";
import AspectDiv from "./AspectDiv";
import EntityProfilePic from "./EntityProfilePic";
import { EventLocation } from "./EventLocation";

export const ActivityCardHostFragment = graphql`
  fragment ActivityCardHostFragment on Entity {
    username
    ...EntityProfilePicFragment @arguments(thumbnail: true)
  }
`;

export const ActivityCardTagListFragment = graphql`
  fragment ActivityCardTagListFragment on Taggable
  @argumentDefinitions(count: { type: "Int", defaultValue: 8 }) {
    tags(first: $count) {
      edges {
        node {
          id
          name
        }
      }
    }
  }
`;

const activityCardVariants = cva("", {
  variants: {
    slot: {
      link: "w-full flex bg-white shadow-[0_1px_3px_0_rgba(16,24,40,0.10),0_1px_2px_0_rgba(16,24,40,0.06)] flex-1",
      thumbnail: "shrink-0 grow-0",
      aspect: "overflow-hidden relative",
      content: "flex flex-col p-5 min-w-0",
    },
    vertical: {
      true: null,
      false: null,
    },
    hasThumbnail: {
      true: null,
      false: null,
    },
  },
  compoundVariants: [
    { slot: "link", vertical: true, class: "flex-col" },
    { slot: "link", vertical: false, class: "flex-row rounded-b-xl" },

    { slot: "thumbnail", vertical: true, class: "w-full" },
    { slot: "thumbnail", vertical: false, class: "w-1/2 rounded-l-xl" },

    { slot: "aspect", vertical: false, class: "rounded-l-xl" },
    { slot: "aspect", hasThumbnail: true, class: "bg-brand-100" },
    { slot: "aspect", hasThumbnail: false, class: "bg-grey-50" },

    { slot: "content", vertical: true, class: "gap-4" },
    { slot: "content", vertical: false, class: "flex-1 justify-center gap-2" },
  ],
  defaultVariants: {
    vertical: true,
    hasThumbnail: false,
  },
});

type ActivityCardLinkVariants = Pick<
  VariantProps<typeof activityCardVariants>,
  "vertical"
>;

const EmptyContentBadge = ({ message }: { message: React.ReactNode }) => (
  <Badge hasPrefix className="inline-flex items-center h-6 px-2 gap-1">
    <SlashCircle01Icon aria-hidden="true" className="w-3 h-3 text-grey-500" />
    <span className="text-sm font-medium leading-tight">{message}</span>
  </Badge>
);

interface ActivityCardTagListProps {
  tags: ActivityCardTagListFragment$key;
}

function ActivityCardTagList(props: ActivityCardTagListProps) {
  const {
    tags: { edges: tags },
  } = useFragment(ActivityCardTagListFragment, props.tags);

  if (!tags || tags.length === 0) {
    return (
      <EmptyContentBadge
        message={<FormattedMessage defaultMessage="No tags yet" />}
      />
    );
  }

  return (
    <ul
      role="list"
      className="min-w-0 overflow-hidden whitespace-nowrap text-ellipsis leading-none"
    >
      {tags.map(({ node }) => (
        <li key={node.id} className="inline-block">
          <Badge className="inline-flex items-center h-6 px-2 mx-1">
            <span className="text-sm font-medium leading-tight">
              {node.name}
            </span>
          </Badge>
        </li>
      ))}
    </ul>
  );
}

interface ActivityCardHostProps {
  host: ActivityCardHostFragment$key;
  label: React.ReactNode;
}

function ActivityCardHost(props: ActivityCardHostProps) {
  const { username, ...host } = useFragment(
    ActivityCardHostFragment,
    props.host,
  );

  return (
    <Link
      to={generatePath("/:username", { username })}
      className="flex flex-row items-center gap-3 rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-400 focus-visible:ring-offset-2"
      onClick={(event) => event.stopPropagation()}
      onMouseDown={(event) => event.stopPropagation()}
    >
      <EntityProfilePic entity={host} size="10" />
      <div className="inline-flex flex-col justify-center items-start">
        <p className="text-grey-700 text-xs font-normal leading-4">
          {props.label}
        </p>
        <p className="text-grey-900 text-sm font-medium leading-5">
          {username}
        </p>
      </div>
    </Link>
  );
}

type ActivityCardContent =
  | { kind: "DESCRIPTION"; shortDescription: string }
  | { kind: "TAGS"; tags: ActivityCardTagListFragment$key }
  | { kind: "EMPTY_DESCRIPTION" }
  | { kind: "EMPTY_TAGS" };

const verticalContent = (
  shortDescription: string | undefined,
  tags: ActivityCardTagListFragment$key | undefined,
): ActivityCardContent =>
  tags
    ? { kind: "TAGS", tags }
    : shortDescription
      ? { kind: "DESCRIPTION", shortDescription }
      : { kind: "EMPTY_TAGS" };

const horizontalContent = (
  shortDescription: string | undefined,
): ActivityCardContent =>
  shortDescription
    ? { kind: "DESCRIPTION", shortDescription }
    : { kind: "EMPTY_DESCRIPTION" };

const activityCardContent = (
  vertical: boolean,
  shortDescription: string | undefined,
  tags: ActivityCardTagListFragment$key | undefined,
): ActivityCardContent =>
  vertical
    ? verticalContent(shortDescription, tags)
    : horizontalContent(shortDescription);

interface ActivityCardContentProps {
  content: ActivityCardContent;
}

const ActivityCardContent = ({ content }: ActivityCardContentProps) => {
  switch (content.kind) {
    case "DESCRIPTION":
      return (
        <p className="text-grey-700 font-normal leading-5 line-clamp-2 wrap-break-word">
          {content.shortDescription}
        </p>
      );
    case "TAGS":
      return <ActivityCardTagList tags={content.tags} />;
    case "EMPTY_DESCRIPTION":
      return (
        <p className="text-grey-500">
          <FormattedMessage defaultMessage="No description yet" />
        </p>
      );
    case "EMPTY_TAGS":
      return (
        <EmptyContentBadge
          message={<FormattedMessage defaultMessage="No tags yet" />}
        />
      );
  }
};

interface ActivityCardThumbnailProps {
  vertical: boolean;
  title: string;
  thumbnail?: string;
  isInviteOnly: boolean;
  overlay?: React.ReactNode;
}

const ActivityCardThumbnail = ({
  vertical,
  thumbnail,
  isInviteOnly,
  overlay,
}: ActivityCardThumbnailProps) => {
  return (
    <div className={activityCardVariants({ slot: "thumbnail", vertical })}>
      <AspectDiv
        className={activityCardVariants({
          slot: "aspect",
          hasThumbnail: !!thumbnail,
          vertical,
        })}
        ratio={2}
      >
        {isInviteOnly && (
          <div className="absolute top-3 right-3">
            <Badge hasPrefix size="lg" shape="pill" className="gap-1">
              <Lock01Icon
                aria-hidden="true"
                className="text-grey-500 h-3 w-3"
              />
              <p className="text-grey-700 text-sm font-medium leading-5">
                <FormattedMessage defaultMessage="Invitation only" />
              </p>
            </Badge>
          </div>
        )}

        {overlay && (
          <div className="absolute bottom-0 left-0 right-0 px-5 py-2 bg-black/40 backdrop-blur-xs inline-flex justify-start items-center gap-1">
            {overlay}
          </div>
        )}

        {thumbnail ? (
          <img
            src={thumbnail}
            className="h-full w-full object-cover object-center"
          />
        ) : (
          <div className="w-full h-full flex flex-col justify-center items-center">
            <div className="flex justify-center items-center w-14 h-14 bg-white rounded-full shadow-[0px_24px_48px_-12px_rgba(16,24,40,0.18)]">
              <Image03Icon
                aria-hidden="true"
                className="text-brand-400 w-8 h-8"
              />
            </div>
          </div>
        )}
      </AspectDiv>
    </div>
  );
};

interface ActivityCardFooterProps {
  host: ActivityCardHostFragment$key;
  label: React.ReactNode;
  footer?: React.ReactNode;
}

const ActivityCardFooter = ({
  host,
  label,
  footer,
}: ActivityCardFooterProps) => (
  <div className="p-5 flex flex-row items-center justify-between">
    <ActivityCardHost host={host} label={label} />

    {footer && (
      <div
        onClick={(event) => event.stopPropagation()}
        onMouseDown={(event) => event.stopPropagation()}
      >
        {footer}
      </div>
    )}
  </div>
);

interface ActivityCardProps extends ActivityCardLinkVariants {
  title: string;
  shortDescription?: string;
  thumbnail?: string;
  url: string;
  host: ActivityCardHostFragment$key;
  tags?: ActivityCardTagListFragment$key;
  location?: EventLocationFragment$key;
  footer?: React.ReactNode;
  hostLabel?: React.ReactNode;
  overlay?: React.ReactNode;
  isInviteOnly?: boolean;
}

export default function ActivityCard({
  title,
  shortDescription,
  thumbnail,
  url,
  host,
  tags,
  location,
  footer,
  overlay,
  vertical = true,
  isInviteOnly = false,
  hostLabel = <FormattedMessage defaultMessage="Created by:" />,
}: ActivityCardProps) {
  const content = activityCardContent(!!vertical, shortDescription, tags);
  const titleId = useId();

  return (
    <article
      className="group min-w-80 flex flex-col w-full h-full rounded-xl border border-grey-200 bg-grey-25 transition-shadow shadow-sm group-hover:shadow-lg group-focus-within:outline-1 group-focus-within:outline-offset-1 group-focus-within:outline-brand-100 overflow-hidden"
      aria-labelledby={titleId}
    >
      <Link
        to={url}
        className={activityCardVariants({ slot: "link", vertical })}
      >
        <ActivityCardThumbnail
          vertical={!!vertical}
          title={title}
          thumbnail={thumbnail}
          isInviteOnly={isInviteOnly}
          overlay={overlay}
        />

        <div className={activityCardVariants({ slot: "content", vertical })}>
          <span
            id={titleId}
            className="text-grey-900 text-lg font-semibold leading-7 transition-colors group-hover:text-brand-500 line-clamp-2 overflow-hidden wrap-break-word"
          >
            {title}
          </span>
          <ActivityCardContent content={content} />
          {location && <EventLocation location={location} />}
        </div>
      </Link>
      <ActivityCardFooter host={host} label={hostLabel} footer={footer} />
    </article>
  );
}
